// Daily-challenge ghost replay: record + fetch top player's run.
// Format: flat array of [t, x, y] triples sampled every SAMPLE_INTERVAL_MS.
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const SAMPLE_INTERVAL_MS = 200; // 5 Hz
const MAX_SAMPLES = 500;              // hard cap, ~100s at 5Hz

// Collector: pass this to GameCanvas via `onSample`
export function makeRecorder() {
  const samples = [];
  let lastMs = -Infinity;
  return {
    onSample: (t, x, y) => {
      const tMs = t * 1000;
      if (tMs - lastMs < SAMPLE_INTERVAL_MS) return;
      lastMs = tMs;
      if (samples.length >= MAX_SAMPLES) return;
      samples.push([+t.toFixed(2), Math.round(x), Math.round(y)]);
    },
    getSamples: () => samples,
    clear: () => (samples.length = 0),
  };
}

// Fetch today's top daily-challenge run (name, score, replay). Null if none.
// If a Handle-of-the-Week challenge is queued (within the last hour), fetch
// that specific handle's ghost instead AND arm the duel state for +2 lives
// on a clean first-attempt win.
import { armDuel } from "@/lib/duel";
export async function fetchTopGhost() {
  try {
    const raw = localStorage.getItem("ppe_challenge_target_v1");
    if (raw) {
      try {
        const target = JSON.parse(raw);
        // Challenge expires in 60 minutes
        const ageMs = Date.now() - (target?.queuedAt || 0);
        if (target?.handle && ageMs < 60 * 60 * 1000) {
          localStorage.removeItem("ppe_challenge_target_v1");
          const res = await axios.get(`${API}/daily/ghost-by-handle`, { params: { handle: target.handle } });
          const data = res.data || null;
          if (data && data.player_name) {
            armDuel(data.player_name, data.score || 0);
          }
          return data;
        }
        // Stale challenge — drop it
        if (ageMs >= 60 * 60 * 1000) localStorage.removeItem("ppe_challenge_target_v1");
      } catch {
        localStorage.removeItem("ppe_challenge_target_v1");
      }
    }
    const res = await axios.get(`${API}/daily/top-ghost`);
    return res.data || null;
  } catch {
    return null;
  }
}

// Interpolate ghost position at time t (seconds) from a samples array.
// Returns { x, y } or null if past the last sample.
export function ghostPositionAt(samples, t) {
  if (!samples || samples.length === 0) return null;
  if (t <= samples[0][0]) return { x: samples[0][1], y: samples[0][2] };
  // Binary search would be cleaner but linear scan is fine for ≤500 pts
  for (let i = 1; i < samples.length; i++) {
    if (samples[i][0] >= t) {
      const [t0, x0, y0] = samples[i - 1];
      const [t1, x1, y1] = samples[i];
      const alpha = (t - t0) / (t1 - t0 || 1);
      return { x: x0 + (x1 - x0) * alpha, y: y0 + (y1 - y0) * alpha };
    }
  }
  return null; // past end
}
